import useEnforceLoggedIn from "@hooks/useEnforceLoggedIn";
import { NotificationType, showNotification } from "@lib/notification";
import FiltersButton from "@ppPartials/filters-button";
import {
    filterAndSortData,
    getDateOptionsFirstVal,
    getDateValueFromOption,
} from "@ppServices/Issue";
import { usePartnerAddRemoveIssueFromExportMutation } from "@ppServices/Partners";
import { Col, Row, Select, Space } from "antd";
import {useEffect, useMemo, useState} from "react";
import _ from "underscore";
import IssueDataTable from "../../partials/issue-data-table";

const IssueDataSection = ({
    dateOptions = [],
    onDataChange,
    queryFunc,
    activeElements,
    setActiveElements,
}) => {

    const { user } = useEnforceLoggedIn();

    const [dateSelect, setDateSelect] = useState<DateSelect | null>(null);

    const [issueIds, setIssueIds] = useState([]);

    const [filteredIds, setFilteredIds] = useState([]);

    const [deselectedIds, setDeselectedIds] = useState<any>([]);

    const [filters, setFilters] = useState<{} | null>({
        publishers: [],
        showVariants: true,
        showRegular: true,
        onlyShowUpdated: false,
        onlyShowNew: false,
        onlyShowPushed: false,
        justFirst: false,
    });

    const [filteredIssues, setFilteredIssues] = useState<any>([]);

    const issueDataQuery = queryFunc({
        userId: user?.id,
        filters: {
            date: dateSelect,
        }
    }, { skip: !dateSelect });

    const [excludeIdFromExport, resultExcludeIdFromExport] = usePartnerAddRemoveIssueFromExportMutation();

    // USE EFFECTS
    useEffect(() => {
        if (issueDataQuery.data) {

            setFilters((prevState) => {
                return {
                    ...prevState,
                    publishers: issueDataQuery
                        .data.publisherIds
                }
            });

        }
    }, [issueDataQuery]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange({
                issue: null,
                selected: null,
                dateSelect: dateSelect,
                ids: {
                    selected: issueIds,
                    deselected: deselectedIds,
                    filtered: filteredIds,
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onDataChange, filteredIds, issueIds, deselectedIds]);

    useEffect(() => {
        if (issueDataQuery.data) {
            let results: any = filterAndSortData(
                issueDataQuery
                    .data?.grpByPublisher,
                filters);

            let filteredIds: any = results
                .map((f) => f.items.map(
                    (f3) => f3.post_id)
                ).flat();

            let filtered = filteredIds
                .filter(f =>
                    !deselectedIds.includes(f)
                );

            if (!_.isEqual(_.sortBy(filtered), _.sortBy(filteredIds)))
                setFilteredIds(filtered);

            if (!_.isEqual(_.sortBy(filtered), _.sortBy(issueIds)))
                setIssueIds(filtered);

            if (!_.isEqual(_.sortBy(results), _.sortBy(filteredIssues)))
                setFilteredIssues(results);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issueDataQuery, filters, deselectedIds]);

    useEffect(() => {
        if (issueDataQuery.data && issueDataQuery.isSuccess) {
            setIssueIds(issueDataQuery.data?.selectedIds);
            setDeselectedIds(issueDataQuery.data?.deselectedIds);
            setFilteredIds([]);
            setActiveElements(issueDataQuery.data?.activeElements);
        }
    }, [issueDataQuery, dateSelect]);

    useEffect(() => {

        if (dateOptions?.length > 0) {
            let [currentWeek] = dateOptions;
            let currentWeekDate: DateSelect = getDateOptionsFirstVal(currentWeek);

            if (!_.isEqual(currentWeekDate, dateSelect))
                setDateSelect(currentWeekDate)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateOptions]);

    useEffect(() => {

        if (resultExcludeIdFromExport.isSuccess) {
            let arg = resultExcludeIdFromExport.originalArgs;
            let message = arg.excluded ? 'Issue Excluded' : 'Issue Added';

            showNotification(message, NotificationType.SUCCESS);
        }

        if (resultExcludeIdFromExport.isError) {
            let message = 'Error Excluded the Issue. Please Try Again';

            showNotification(message, NotificationType.ERROR);
        }

    }, [resultExcludeIdFromExport]);

    // EVENTS
    const handleDateChange = (obj) => {
        const newDateValue = getDateValueFromOption(obj);

        if (!_.isEqual(newDateValue, dateSelect))
            setDateSelect(newDateValue);
    };

    const handleSelectChange = ({ issue, selected, ids }) => {
        if (!_.isEqual(_.sortBy(ids.selected), _.sortBy(issueIds)))
            setIssueIds(ids.selected);

        if (!_.isEqual(_.sortBy(ids.filtered), _.sortBy(filteredIds)))
            setFilteredIds(ids.filtered);
        
        if (!_.isEqual(_.sortBy(ids.deselected), _.sortBy(deselectedIds)))
            setDeselectedIds(ids.deselected);

        if (issue && user.id) {
            excludeIdFromExport({
                userId: user.id,
                issueId: issue.post_id,
                excluded: !selected
            })
        }
    };

    const handleClearFilters = () => {
        let pids = issueDataQuery
            ?.data
            ?.publisherIds;

        setFilters({
            publishers: pids,
            showVariants: true,
            showRegular: true,
            onlyShowUpdated: false,
            onlyShowNew: false,
            justFirst: false,
        });
    };

    const publishers = useMemo(() => {
        return issueDataQuery?.data?.publishersOpitons || [];
    }, [issueDataQuery]);

    return (
        <Row
            className={`pp-new-release-section`}
            justify={`space-between`}
        >
            <Col span={24}>
                <Row justify={`space-between`} className={`mar-bot-10`}>
                    <Col>
                        <Space direction={`horizontal`} size={24}>
                            <Select
                                loading={dateOptions.length === 0}
                                onChange={handleDateChange}
                                options={dateOptions}
                                value={dateSelect ? {
                                    label: dateSelect.middle,
                                    value: dateSelect.start + ', ' + dateSelect.end
                                } : null}
                                placeholder="Select Week"
                                defaultActiveFirstOption
                                style={{ width: 200 }}
                                notFoundContent={null}
                                labelInValue
                            />
                        </Space>
                    </Col>
                    <Col>
                        <Space direction={`horizontal`} size={24}>
                            <span className={`count`}>
                                Items: {issueIds?.length} / {issueDataQuery
                                    ?.data?.overview
                                    ?.total}
                            </span>
                            <FiltersButton
                                filters={filters}
                                setFilters={setFilters}
                                onFilterClear={handleClearFilters}
                                publishers={publishers}
                            />
                        </Space>
                    </Col>
                </Row>
            </Col>
            <Col span={24}>
                <IssueDataTable
                    isLoading={
                        issueDataQuery.isLoading ||
                        issueDataQuery.isFetching
                    }
                    onSelectChange={handleSelectChange}
                    expandedRowIds={
                        issueDataQuery
                            .data?.publisherIds
                    }
                    selectedRowIds={
                        issueDataQuery
                            .data?.selectedIds
                    }
                    deselectedRowIds={
                        issueDataQuery
                            .data?.deselectedIds
                    }
                    dataSource={filteredIssues}
                    activeElements={activeElements}
                />
            </Col>
        </Row>
    );
};

export default IssueDataSection;